/**
 * FallbackBadge — muted ochre badge for positions without specific data.
 *
 * Displayed when a school has no fine-position data and the score shown
 * is the coarse parent position estimate. Tooltip communicates the context.
 *
 * Design: ochre/warm-muted background with the parent-position label in the
 * top-left corner (e.g., "~DEF", "~MID", "~FWD"), matching the ScoreBadge
 * rectangular shape but without the dark pill treatment. Uses the brand-accent
 * color (#A0671E) for the label and tilde, ink-700 for the score number.
 *
 * Size variants:
 *   sm — 44×30px, score 16px, label 9px  (mobile)
 *   md — 56×40px, score 22px, label 9px  (desktop, default)
 *
 * Accessibility: aria-label describes the fallback context. All inner elements
 * are aria-hidden.
 */

import React from 'react';

interface Props {
  score: number;
  size?: 'sm' | 'md';
  /** Parent coarse position slug ('def' | 'mid' | 'fwd' | 'gk'). Used for the corner label. */
  parentPosition: string;
}

interface SizeConfig {
  width: string;
  height: string;
  scoreFontSize: string;
  labelFontSize: string;
}

const SIZE_CONFIG: Record<string, SizeConfig> = {
  sm: { width: 'w-11',  height: 'h-[30px]', scoreFontSize: 'text-base',   labelFontSize: 'text-[9px]' },
  md: { width: 'w-14',  height: 'h-10',     scoreFontSize: 'text-[22px]', labelFontSize: 'text-[9px]' },
};

export function FallbackBadge({ score, size = 'md', parentPosition }: Props) {
  const { width, height, scoreFontSize, labelFontSize } = SIZE_CONFIG[size] ?? SIZE_CONFIG.md;
  const roundedScore = Math.round(score);
  const cornerLabel = `~${parentPosition.toUpperCase()}`;

  return (
    <span
      className={`inline-flex flex-col items-center justify-center ${width} ${height} bg-tier-fallback-bg rounded border border-hairline`}
      title={`Score shown is the ${cornerLabel.slice(1)} parent-position estimate; no specific position data`}
      aria-label={`Score estimate: ${roundedScore} — ${cornerLabel.slice(1)} parent position data (no specific position data)`}
    >
      {/* Parent-position label — top, centered, brand-accent color. Stacked above the
          score for visual parity with ScoreBadge's position-above-score layout. */}
      <span
        aria-hidden="true"
        className={`${labelFontSize} font-bold tracking-widest text-brand-accent leading-none`}
        style={{ letterSpacing: '0.1em' }}
      >
        {cornerLabel}
      </span>

      {/* Score number — centered, below the position label */}
      <span
        aria-hidden="true"
        className={`${scoreFontSize} font-bold tabular-nums text-gray-700 leading-none mt-1`}
      >
        {roundedScore}
      </span>
    </span>
  );
}
